import classNames from "classnames";
import React, { MouseEventHandler, ReactNode, useMemo, useRef } from "react";

import { usePlayContext } from "../../pages/Play/play-context";
import { BasketballActionType, FrameStencil, MarkerType, MidPoint, Point, PointType, StencilType } from "../../types";
import { ACTION_RANGE } from "../../utils";
import { usePlayingSurfaceContext } from "../playing-surface/playing-surface-context";
import { useMoveStencil, useMoveStencilNodePoint, useRotateStencil } from "./hooks";
import styles from "./stencil-wrapper.module.scss";

const SELECT_POINT_SIZE = 1.25;

export interface ChildrenProps {
  controlPoints: MidPoint[];
}

export interface StencilWrapperProps {
  stencil: FrameStencil;
  selectionIconOffset?: Partial<Point>;
  rotationCenter?: Point;
  controlPoints?: MidPoint[];
  onDoubleClick?: MouseEventHandler<SVGGElement>;
  children: ((props: ChildrenProps) => ReactNode) | ReactNode;
  snapToAngleOnRotate?: boolean;
}

export const StencilWrapper = ({
  stencil,
  selectionIconOffset = {},
  rotationCenter = { x: 0, y: 0 },
  controlPoints,
  onDoubleClick,
  children,
  snapToAngleOnRotate = false,
}: StencilWrapperProps) => {
  const { id } = stencil;
  const { x: selectionIconOffsetX = 0, y: selectionIconOffsetY = 0 } = selectionIconOffset;

  const isPlayer = stencil.stencilType === StencilType.MARKER && stencil.markerType === MarkerType.PLAYER;
  const isMultiPointStencil =
    (stencil.stencilType === StencilType.ACTION && stencil.layerType !== BasketballActionType.HANDOFF) ||
    (stencil.stencilType === StencilType.MARKER &&
      (stencil.markerType === MarkerType.SHADING_RECTANGLE ||
        stencil.markerType === MarkerType.SHADING_OVAL ||
        stencil.markerType === MarkerType.SHADING_TRAPEZOID));

  const { selectItemApi, drawToolbarApi, frameStencilsApi } = usePlayContext();
  const { selectItem, selectedItems } = selectItemApi;
  const { fastPlace, freeDraw } = drawToolbarApi;
  const { isActive: fastPlaceEnabled, toggle: toggleFastPlace } = fastPlace;
  const { updatePossession } = frameStencilsApi;

  const { isEditable } = usePlayingSurfaceContext();

  const stencilWrapperRef = useRef<SVGGElement>(null);

  const isSelected = useMemo(() => selectedItems.includes(id), [selectedItems, id]);

  // on click, select the item
  const handleClick: MouseEventHandler<SVGGElement> = () => {
    if (isEditable) {
      if (fastPlaceEnabled && isPlayer) {
        updatePossession({ selectedItems: [id] });
        toggleFastPlace();
      } else {
        selectItem(id);
      }
    }
  };

  // get the temp position of the stencil that is controlled by both parent rendering and by dragging
  const { groupTempPos } = useMoveStencil({ stencil, stencilWrapperRef });

  // get the temp control points of the stencil that is controlled by both parent rendering and by dragging nodes
  const { tempControlPoints } = useMoveStencilNodePoint({ stencilWrapperRef, controlPoints, stencil });

  const rotationDraggerRef = useRef<SVGCircleElement>(null);
  const { tempRotation } = useRotateStencil({
    stencilWrapperRef,
    rotationDraggerRef,
    stencil,
    snapToAngle: snapToAngleOnRotate,
  });

  return (
    <g
      id={`${isEditable ? "editable-play-surface" : ""}${id}`}
      ref={stencilWrapperRef}
      onClick={handleClick}
      onDoubleClick={onDoubleClick}
      transform={`translate(${groupTempPos.x}, ${groupTempPos.y}) rotate(${tempRotation || 0}, ${rotationCenter.x}, ${
        rotationCenter.y + selectionIconOffsetY
      })`}
      className={classNames("stencil-wrapper", styles.stencilWrapper, {
        [styles.isEditable || ""]: isEditable && !(freeDraw.isActive && isPlayer),
        [styles.stencilSelected || ""]: isSelected,
      })}
    >
      {children instanceof Function ? children({ controlPoints: tempControlPoints }) : children}

      {!isMultiPointStencil && (
        <circle
          ref={rotationDraggerRef}
          className={styles.selectionIcon}
          cx={selectionIconOffsetX}
          cy={-1 + selectionIconOffsetY}
          r={0.25}
        />
      )}
      {isPlayer && <circle className={classNames("association-circle", styles.associationCircle)} r={ACTION_RANGE} />}

      {isMultiPointStencil && (
        <>
          <circle className={classNames("association-circle-start", styles.associationCircle)} r={ACTION_RANGE} />
          <circle
            className={classNames("association-circle-end", styles.associationCircle)}
            cx={tempControlPoints[tempControlPoints.length - 1]?.x}
            cy={tempControlPoints[tempControlPoints.length - 1]?.y}
            r={ACTION_RANGE}
          />

          {tempControlPoints.map((point, i) =>
            point.pointType === PointType.CURVE ? (
              <circle
                data-point-type={point.pointType}
                data-point-index={i}
                cx={point.x}
                cy={point.y}
                r={SELECT_POINT_SIZE / 2}
                className={classNames(styles.selectPoint, "select-point")}
                key={i}
              />
            ) : (
              <rect
                transform={`translate(${-SELECT_POINT_SIZE / 2}, ${-SELECT_POINT_SIZE / 2})`}
                data-point-type={point.pointType}
                data-point-index={i}
                x={point.x}
                y={point.y}
                width={SELECT_POINT_SIZE}
                height={SELECT_POINT_SIZE}
                className={classNames(styles.selectPoint, "select-point", {
                  [styles.startPoint || ""]: point.pointType === PointType.START,
                  [styles.endPoint || ""]: point.pointType === PointType.END,
                  [styles.anglePoint || ""]: point.pointType === PointType.ANGLE,
                })}
                key={i}
              />
            )
          )}
        </>
      )}
    </g>
  );
};
